/* eslint-disable no-console */
import { useBrand, useQuery } from '@contexts/index'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

const SearchBar = forwardRef(({ value, hidden, onSearchDataUpdate }, ref) => {
  const searchInput = useRef(value)
  const [searchDealerName, setSearchDealerName] = useState(false)
  const [nameSearch, setNameSearch] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [isDealerFound, setDealerFound] = useState(true)
  const [dealersCount, setDealersCount] = useState(0)
  const lastSearchCoords = useRef(null)
  const brand = useBrand()
  const query = useQuery()
  const queryRef = useRef(query)

  // Keep queryRef in sync with query
  useEffect(() => {
    queryRef.current = query
  }, [query])

  useEffect(() => {
    if (!searchInput) {
      searchInput.current.value = value
    }
  }, [value])

  const getDealersList = async (coords, callback) => {
    setIsSearching(true)
    lastSearchCoords.current = coords

    const currentGroup = queryRef.current?.group

    const url = currentGroup
      ? `https://kong-proxy-intranet.toyota-europe.com/dxp/dealers/api/${brand}/gb/en/drive/${coords.lon}/${coords.lat}?count=5000&isCurrentLocation=false&services=showroom`
      : `https://kong-proxy-intranet.toyota-europe.com/dxp/dealers/api/${brand}/gb/en/drive/${coords.lon}/${coords.lat}?count=4&isCurrentLocation=false&services=showroom`

    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const data = await response.json()

      if (currentGroup) {
        data.dealers = data.dealers
          .filter((i) => i.parent === currentGroup)
          .slice(0, 4)
        data.total = data.dealers.length
      }

      data.dealers = data.dealers.filter((i) =>
        i.services.some((j) => j.service === 'ShowRoom'),
      )

      callback(data)
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setIsSearching(false)
    }
  }

  useImperativeHandle(ref, () => ({
    triggerSearch: () => {
      if (lastSearchCoords.current) {
        getDealersList(lastSearchCoords.current, (result) => {
          setDealersCount(result.dealers.length)
          setDealerFound(result.dealers.length > 0)
          onSearchDataUpdate(result.dealers)
        })
      } else {
        getDealersList({ lat: 51.5074, lon: -0.1278 }, (result) => {
          setDealersCount(result.dealers.length)
          setDealerFound(result.dealers.length > 0)
          onSearchDataUpdate(result.dealers)
        })
      }
    },
  }))

  // SEARCH BY POSTCODE
  const searchByPostcode = (term) => {
    window.customMap.geocoder
      .search(term, { countryCode: ['GB', 'IM', 'JE', 'GG'] })
      .then((geo) => {
        if (geo.length) {
          const lon = geo[0].coordinates[0]
          const lat = geo[0].coordinates[1]
          getDealersList({ lat, lon }, (result) => {
            setDealersCount(result.dealers.length)
            setIsSearching(false)
            setDealerFound(result.dealers.length > 0)
            onSearchDataUpdate(result.dealers)
          })
        } else {
          setDealerFound(false)
        }
      })
  }

  useEffect(() => {
    window.customMap = new mapsWrapper.DynamicMap(
      document.createElement('div'),
      {
        accessToken:
          brand === 'lexus'
            ? process.env.NEXT_PUBLIC_LEXUS_MAPBOX_TOKEN
            : process.env.NEXT_PUBLIC_TOYOTA_MAPBOX_TOKEN,
        countryCode: ['GB', 'IM', 'JE', 'GG'],
        language: 'en',
      },
    )

    // add auto complete
    if (!searchDealerName) {
      window.customMapAutoComplete = new mapsWrapper.AutoComplete({
        autoCompleteInput: document.getElementById('postcodeSearch'),
        accessToken:
          brand === 'lexus'
            ? process.env.NEXT_PUBLIC_LEXUS_MAPBOX_TOKEN
            : process.env.NEXT_PUBLIC_TOYOTA_MAPBOX_TOKEN,
        countryCode: ['GB', 'IM', 'JE', 'GG'],
        types: 'place,locality,neighborhood,region,postcode',
        onLocationSelect: (location) => {
          if (location) {
            const lon = location.coordinates[0]
            const lat = location.coordinates[1]
            getDealersList({ lat, lon }, (result) => {
              setDealersCount(result.dealers.length)
              setIsSearching(false)
              setDealerFound(result.dealers.length > 0)
              onSearchDataUpdate(result.dealers)
            })
          }
        },
      })
    }
  }, [searchDealerName])

  // SEARCH BY DEALERNAME
  const searchByName = async () => {
    setIsSearching(true)
    const currentGroup = queryRef.current?.group

    const url = currentGroup
      ? `https://kong-proxy-intranet.toyota-europe.com/dxp/dealers/api/${brand}/gb/en/find/name/${nameSearch}?count=5000&extraCountries=&isCurrentLocation=false&services=showroom`
      : `https://kong-proxy-intranet.toyota-europe.com/dxp/dealers/api/${brand}/gb/en/find/name/${nameSearch}?count=4&extraCountries=&isCurrentLocation=false&services=showroom`

    try {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const data = await response.json()

      if (currentGroup) {
        data.dealers = data.dealers
          .filter((i) => i.parent === currentGroup)
          .slice(0, 4)
        data.total = data.dealers.length
      }

      data.dealers = data.dealers.filter((i) =>
        i.services.some((j) => j.service === 'ShowRoom'),
      )

      setDealersCount(data.dealers.length)
      setIsSearching(false)
      setDealerFound(data.dealers.length > 0)
      onSearchDataUpdate(data.dealers)
    } catch (error) {
      if (dealersCount < 0) console.error('Error:', error)
    } finally {
      setIsSearching(false)
    }
  }
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (searchDealerName) {
        searchByName()
      } else {
        searchByPostcode(searchInput.current.value)
      }
    }
  }

  // SEARCH BY LOCATION
  const handleUseLocation = () => {
    setIsSearching(true)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords

          // Check if latitude and longitude are available
          if (latitude && longitude) {
            const currentGroup = queryRef.current?.group
            const url = currentGroup
              ? `https://kong-proxy-intranet.toyota-europe.com/dxp/dealers/api/${brand}/gb/en/drive/${longitude}/${latitude}?count=5000&extraCountries=&isCurrentLocation=false&services=showroom`
              : `https://kong-proxy-intranet.toyota-europe.com/dxp/dealers/api/${brand}/gb/en/drive/${longitude}/${latitude}?count=4&extraCountries=&isCurrentLocation=false&services=showroom`

            try {
              const response = await fetch(url)
              if (!response.ok) {
                throw new Error('Network response was not ok')
              }
              const data = await response.json()

              if (currentGroup) {
                data.dealers = data.dealers
                  .filter((i) => i.parent === currentGroup)
                  .slice(0, 4)
                data.total = data.dealers.length
              }

              data.dealers = data.dealers.filter((i) =>
                i.services.some((j) => j.service === 'ShowRoom'),
              )

              setDealersCount(data.dealers.length)
              setIsSearching(false)
              setDealerFound(data.dealers.length > 0)
              onSearchDataUpdate(data.dealers)
            } catch (error) {
              console.error('Error in searchByLocation:', error)
            } finally {
              setIsSearching(false)
            }
          } else {
            console.error('Latitude or Longitude is not available.')
            // Handle the case where latitude or longitude is not available
            setIsSearching(false)
          }
        },
        (error) => {
          console.error('Error getting location:', error.message)
          // Handle error if needed
          setIsSearching(false)
        },
      )
    } else {
      console.error('Geolocation is not supported by your browser')
      // Handle lack of geolocation support if needed
      setIsSearching(false)
    }
  }

  const handleChangeSearchOption = (option) => {
    setSearchDealerName(option)
    setDealerFound(true)
  }

  if (hidden) {
    return null
  }

  return (
    <div className={`relative ${isSearching ? 'opacity-50' : ''}`}>
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between my-6 gap-3">
        <div className="hidden lg:block">
          <h2 className="text-xl">
            {`Find your ${brand === 'lexus' ? 'centre' : 'dealer'}`}
          </h2>
          <p>
            Please fill in the required fields
            <span className="text-red">*</span>
          </p>
        </div>
        <div
          className="items-start justify-top mb-4 md:mb-2"
          data-gt-name="componentevent"
          style={{
            display: searchDealerName ? 'block' : 'none',
          }}
        >
          <div className="md:col-span-2 lg:col-span-1">
            <label className="text-xs">
              {`Search by ${brand === 'lexus' ? 'centre' : 'dealer'} name`}
            </label>
            <div>
              <input
                type="text"
                className="p-4 h-[48px] mt-2 pr-[52px] w-[100%] text-ellipsis overflow-hidden"
                data-gt-action="text-input-interaction"
                data-gt-label={`places`}
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              {isSearching && (
                <div className="loader-container absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="loader dark">Loading...</div>
                </div>
              )}
            </div>
            {!isDealerFound && (
              <div className="text-red text-tiny flex justify-between pt-1 lg:absolute">
                {' '}
                <span className="text-tiny">
                  {`No ${brand === 'lexus' ? 'Centres' : 'Dealers'} found`}.
                </span>
              </div>
            )}
          </div>
        </div>
        <div
          className="items-start justify-top mb-4 md:mb-2"
          data-gt-name="componentevent"
          style={{
            display: searchDealerName ? 'none' : 'block',
          }}
        >
          <div className="md:col-span-2 lg:col-span-1">
            <label className="text-xs">Search by city or postcode</label>
            <div>
              <input
                type="text"
                id={`postcodeSearch`}
                ref={searchInput}
                className="p-4 h-[48px] mt-2 pr-[52px] w-[100%] text-ellipsis overflow-hidden"
                data-gt-action="text-input-interaction"
                data-gt-label={`places`}
                onKeyDown={handleKeyDown}
              />
              {isSearching && (
                <div className="loader-container absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="loader dark">Loading...</div>
                </div>
              )}
            </div>
            {!isDealerFound && (
              <div className="text-red text-tiny flex justify-between pt-1 lg:absolute">
                {' '}
                <span className="text-tiny">No location found.</span>
              </div>
            )}
          </div>
        </div>
        <label className="flex flex-row text-sm lg:ml-5 gap-2 cursor-pointer">
          <input
            className="w-5 h-5 text-base cursor-pointer hover:bg-base-hover checked:border-base checked:bg-transparent checked:bg-[url('/img/circle.svg')] checked:hover:bg-transparent checked:hover:border-base checked:focus:bg-transparent checked:focus:border-base"
            type="radio"
            name="searchOption"
            value="false"
            checked={!searchDealerName}
            onChange={() => handleChangeSearchOption(false)}
            data-gt-name="componentevent"
            data-gt-componentgroup="forms"
            data-gt-action="radio-input-interaction"
            data-gt-label="search-postcode"
          />
          Search by city or postcode
        </label>
        {!queryRef.current?.group && (
          <label className="flex flex-row text-sm lg:ml-5 gap-2 cursor-pointer">
            <input
              className="w-5 h-5 text-base cursor-pointer hover:bg-base-hover checked:border-base checked:bg-transparent checked:bg-[url('/img/circle.svg')] checked:hover:bg-transparent checked:hover:border-base checked:focus:bg-transparent checked:focus:border-base"
              type="radio"
              name="searchOption"
              value="true"
              checked={searchDealerName}
              onChange={() => handleChangeSearchOption(true)}
              data-gt-name="componentevent"
              data-gt-componentgroup="forms"
              data-gt-action="radio-input-interaction"
              data-gt-label="search-name"
            />
            {`Search by ${brand === 'lexus' ? 'centre' : 'dealer'} name`}
          </label>
        )}
        <div className="mb-1">
          <button
            className="text-xs inline-flex items-center"
            onClick={() => handleUseLocation()}
            data-gt-name="componentevent"
            data-gt-componentgroup="forms"
            data-gt-action="click-cta"
            data-gt-label="use-location"
          >
            <p className="inline-flex items-center underline">
              Use my current location
              <span className="ml-1">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3 8.79688L9.7 10.3969L11.6 17.2969L17 3.29688L3 8.79688Z"
                    stroke="#6C6F76"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </p>
          </button>
        </div>
      </div>
      <hr style={{ border: '1px solid #ddd' }} />
    </div>
  )
})

SearchBar.displayName = 'SearchBar'

export default SearchBar
